/*
 * ─── ไม้บรรทัด + เส้นไกด์: the numbers (no DOM, no Fabric — the tests load this) ─────────────────────────────────────
 * Choices (22 ต.ค. 2569): 1ก guides saved in the work, each page its own (src/editor/pageFormat.ts) · 2ก the unit is
 * automatic (a page with a print bleed → mm, else px) and can be switched px/mm/cm · 3ก a switch in เพิ่มเติม,
 * remembered on the device · 4ก dragging a guide back onto its ruler removes it; objects snap to guides.
 * Rulers count from the page's top-left corner (the trim line), like the size chip.
 */
import { MM_PER_INCH, PRINT_DPI } from '../print/rules';

export type RulerUnit = 'px' | 'mm' | 'cm';
/** what the person picked on the ruler corner ('auto' = by the page) */
export type RulerChoice = 'auto' | RulerUnit;
export const RULER_CHOICES: RulerChoice[] = ['auto', 'px', 'mm', 'cm'];
/** the rulers' thickness on the screen (css px) */
export const RULER_PX = 20;
/** a guide dragged closer than this to a page edge/centre or an object edge (screen px) lands on it */
export const GUIDE_SNAP_PX = 8;
/** a finger/pointer this close to a guide (screen px) grabs it; a mouse needs less */
export const GUIDE_GRAB_TOUCH = 12, GUIDE_GRAB_MOUSE = 5;

/** the unit shown: the person's choice, or by the page — a page with a print bleed is a print job (mm) */
export function rulerUnit(choice: RulerChoice, bleedMm: number | undefined): RulerUnit {
  if (choice !== 'auto') return choice;
  return (bleedMm ?? 0) > 0 ? 'mm' : 'px';
}

/** design px in one unit (mm/cm at the print resolution, src/print/rules.ts) */
export function pxPerUnit(u: RulerUnit): number {
  if (u === 'px') return 1;
  const mm = PRINT_DPI / MM_PER_INCH;
  return u === 'mm' ? mm : mm * 10;
}

/**
 * The tick spacing for a zoom: `major` (a number every major tick) in the unit — 1, 2 or 5 × 10ⁿ, the smallest whose
 * gap on the screen is at least `minGap` css px — and how many parts it is cut into for the small ticks (0 = none,
 * when they would be closer than 5 px).
 */
export function tickPlan(zoom: number, u: RulerUnit, minGap = 56): { major: number; parts: number } {
  const screenPerUnit = zoom * pxPerUnit(u);
  if (!(screenPerUnit > 0) || !Number.isFinite(screenPerUnit)) return { major: 1, parts: 0 };
  const want = minGap / screenPerUnit;
  let p = Math.pow(10, Math.floor(Math.log10(want)));
  let major = p;
  for (const m of [1, 2, 5, 10]) { if (m * p >= want - 1e-9) { major = m * p; break; } }
  // px rulers never show fractions of a pixel
  if (u === 'px' && major < 1) major = 1;
  p = major;
  const parts = [10, 5, 2].find(n => (p / n) * screenPerUnit >= 5 && (u !== 'px' || Number.isInteger(p / n))) ?? 0;
  return { major, parts };
}

/** a ruler number: whole numbers without decimals, small steps with as few decimals as they need */
export function tickLabel(v: number, major: number): string {
  const dec = major >= 1 ? 0 : Math.min(3, Math.ceil(-Math.log10(major) - 1e-9));
  const s = v.toFixed(dec);
  return s === '-0' ? '0' : s;
}

/** a guide position for showing (in the unit, at most 1 decimal for mm/cm) */
export function guideLabel(px: number, u: RulerUnit): string {
  const v = px / pxPerUnit(u);
  return u === 'px' ? `${Math.round(v)} px` : `${(Math.round(v * 10) / 10).toFixed(u === 'cm' ? 2 : 1)} ${u}`;
}

/** the nearest line within `th` (design px) of `v`, else `v` (and whether it snapped) */
export function snapTo(v: number, lines: number[], th: number): { v: number; snapped: boolean } {
  let best: number | null = null;
  for (const c of lines) if (Math.abs(c - v) <= th && (best === null || Math.abs(c - v) < Math.abs(best - v))) best = c;
  return best === null ? { v, snapped: false } : { v: best, snapped: true };
}

/** the guide (index) on `axis` closest to `v` within `th` (design px), or -1 */
export function guideAt(list: number[], v: number, th: number): number {
  let k = -1;
  list.forEach((g, i) => { if (Math.abs(g - v) <= th && (k < 0 || Math.abs(g - v) < Math.abs(list[k] - v))) k = i; });
  return k;
}

/**
 * Where a guide being dragged ends: 'remove' when the pointer is back over its own ruler (or off the work area on the
 * ruler's side) — choice 4ก; else 'keep'. `along` = the pointer's distance from the work area's top (horizontal guide)
 * or left (vertical guide) edge in css px, rulers included.
 */
export const dropGuide = (along: number) => along < RULER_PX ? 'remove' : 'keep';
